import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Lock, Mail, Clock, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import AuthDialog from '@/components/AuthDialog';

const GroupInvite = () => {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    fetchInvite();
  }, [inviteId]);

  const fetchInvite = async () => {
    try {
      const response = await axios.get(`${API}/groups/invite/${inviteId}`);
      setInvite(response.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setError('Invitation not found');
      } else if (status === 410) {
        setError(err.response?.data?.detail || 'This invitation has expired or already been used');
      } else {
        setError('Failed to load invitation');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    // Check if user's email matches invite email
    if (user.email.toLowerCase() !== invite.email.toLowerCase()) {
      toast.error(`This invitation was sent to ${invite.email}. Please sign in with that email address.`);
      return;
    }

    setAccepting(true);
    try {
      const response = await axios.post(`${API}/groups/invite/${inviteId}/accept`);
      toast.success(`Welcome to ${invite.group.name}!`);
      navigate(`/groups/${response.data.group_slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 pb-20 px-4 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 pb-20 px-4">
          <div className="max-w-md mx-auto">
            <Card className="border-2 border-red-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="text-xl font-bold mb-2">Invitation Unavailable</h2>
                  <p className="text-muted-foreground mb-6">{error}</p>
                  <Button onClick={() => navigate('/groups')} variant="outline">
                    Browse Groups
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="border-2 border-border overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-br from-purple-600 to-amber-500 p-6 text-white text-center">
                <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8" />
                </div>
                <h1 className="text-2xl font-bold mb-1">Private Group Invitation</h1>
                <p className="text-white/80 text-sm">You've been invited to join an exclusive group</p>
              </div>

              <CardContent className="p-6">
                {/* Group Info */}
                <div className="bg-gradient-to-br from-purple-50 to-amber-50 rounded-xl p-4 mb-6 border border-purple-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="text-3xl">{invite.group.icon || '🔒'}</div>
                    <div>
                      <h2 className="font-bold text-lg">{invite.group.name}</h2>
                      <p className="text-sm text-muted-foreground">
                        <Users className="w-4 h-4 inline mr-1" />
                        {invite.group.member_count} member{invite.group.member_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  {invite.group.description && (
                    <p className="text-sm text-gray-600">{invite.group.description}</p>
                  )}
                </div>

                {/* Invited By */}
                <div className="flex items-center gap-3 mb-6 p-3 bg-gray-50 rounded-lg">
                  {invite.invited_by?.profile_picture ? (
                    <img 
                      src={invite.invited_by.profile_picture} 
                      alt="" 
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <span className="text-purple-600 font-medium">
                        {invite.invited_by?.name?.[0] || '?'}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Invited by</p>
                    <p className="font-medium">{invite.invited_by?.name || 'A group admin'}</p>
                  </div>
                </div>

                {/* Expiry Notice */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                  <Clock className="w-4 h-4" />
                  <span>Expires on {formatDate(invite.expires_at)}</span>
                </div>

                {/* Email Notice */}
                <div className="flex items-center gap-2 text-sm bg-blue-50 text-blue-700 p-3 rounded-lg mb-6">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span>This invitation was sent to <strong>{invite.email}</strong></span>
                </div>

                {/* Action Button */}
                {user ? (
                  user.email.toLowerCase() === invite.email.toLowerCase() ? (
                    <Button 
                      onClick={handleAccept} 
                      className="w-full h-12 text-lg bg-gradient-to-r from-purple-600 to-amber-500 hover:from-purple-700 hover:to-amber-600"
                      disabled={accepting}
                    >
                      {accepting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Joining...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Accept & Join Group
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-amber-50 text-amber-800 p-3 rounded-lg text-sm">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        You're signed in as <strong>{user.email}</strong>, but this invitation was sent to <strong>{invite.email}</strong>.
                      </div>
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => {
                          // Sign out and show auth dialog
                          localStorage.removeItem('token');
                          window.location.reload();
                        }}
                      >
                        Sign in with {invite.email}
                      </Button>
                    </div>
                  )
                ) : (
                  <div className="space-y-3">
                    <Button 
                      onClick={() => setShowAuth(true)}
                      className="w-full h-12 text-lg bg-gradient-to-r from-purple-600 to-amber-500 hover:from-purple-700 hover:to-amber-600"
                    >
                      Sign In to Accept
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    <p className="text-center text-sm text-muted-foreground">
                      Don't have an account? You can create one when signing in.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      <Footer />

      {/* Auth Dialog */}
      <AuthDialog 
        isOpen={showAuth} 
        onClose={() => setShowAuth(false)}
        defaultMode="signup"
        prefillEmail={invite?.email}
      />
    </div>
  );
};

export default GroupInvite;
